<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import logo from "@/assets/logo.png";
import flagUs from "@/assets/us.png";
import flagBr from "@/assets/br.png";
import api from '@/services/api';
import { useRouter, useRoute } from 'vue-router';

const router = useRouter();
const route = useRoute();

const props = defineProps({
  language: { type: String, default: 'pt' },
  isPreview: { type: Boolean, default: false },
  showLangSelector: { type: Boolean, default: true },
  navbarBgImage: { type: String, default: '' }
});

const isScrolled = ref(false);
const showMobileMenu = ref(false);
const handleScroll = () => {
  isScrolled.value = window.scrollY > 50;
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll);
  fetchPresskits();
});
onUnmounted(() => window.removeEventListener('scroll', handleScroll));

const scrollTo = (id) => {
  const isEn = props.language === 'en';
  const homePath = isEn ? '/en/home' : '/home';
  const currentPath = route.path;

  if (currentPath === homePath || currentPath === '/' || (currentPath === '' && homePath === '/home')) {
    const targetId = id.replace('#', '');
    const el = document.getElementById(targetId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  } else {
    router.push({ path: homePath, hash: id });
  }
  showMobileMenu.value = false;
};

const menuItems = computed(() => {
  const isEn = props.language === 'en';
  return [
    { label: isEn ? 'HOME' : 'INÍCIO', id: '#hero-section' },
    { label: isEn ? 'GAMES' : 'JOGOS', id: '#games-section' },
    { label: isEn ? 'ABOUT' : 'SOBRE', id: '#about-section' },
    { label: isEn ? 'CONTACT' : 'CONTATO', id: '#contact-section' }
  ];
});

const presskits = ref([]);
const showPresskitDropdown = ref(false);

const fetchPresskits = async () => {
  try {
    const { data } = await api.getPages({ type: 'presskit' });
    const unique = [];
    const seen = new Set();
    for (const item of data || []) {
      if (item.customUrl && !seen.has(item.customUrl)) {
        seen.add(item.customUrl);
        unique.push(item);
      }
    }
    presskits.value = unique;
  } catch (error) {
    console.error("Error fetching presskits:", error);
  }
};

const goToPresskit = (slug) => {
  router.push(`/pages/${slug}`);
  showPresskitDropdown.value = false;
};

const currentLangLabel = computed(() => props.language === 'en' ? 'ENGLISH' : 'PORTUGUÊS');
const currentFlag = computed(() => props.language === 'en' ? flagUs : flagBr);

const showLangDropdown = ref(false);
const toggleLangDropdown = () => showLangDropdown.value = !showLangDropdown.value;

// Close dropdown on click outside
const closeDropdown = (e) => {
  if (!e.target.closest('.lang-selector-container')) {
    showLangDropdown.value = false;
  }
};
onMounted(() => window.addEventListener('click', closeDropdown));
onUnmounted(() => window.removeEventListener('click', closeDropdown));

const languages = [
  { label: 'PORTUGUÊS', code: 'pt', flag: flagBr },
  { label: 'ENGLISH', code: 'en', flag: flagUs }
];

const changeLang = (code) => {
  showLangDropdown.value = false;
  let newPath = route.path;
  if (code === 'pt') {
    newPath = route.path.replace(/^\/en/, '');
    if (newPath === '') newPath = '/home';
  }
  router.push({
    path: newPath,
    query: {
      ...route.query,
      lang: code === 'pt' ? undefined : 'en'
    }
  });
};
</script>

<template>
  <nav 
    :class="[isPreview ? 'sticky' : 'fixed', 'top-0 left-0 w-full z-[100] bg-[#60269E] shadow-lg transition-all duration-300 flex items-center h-20 md:h-24']"
  >
    <!-- Background Pattern (Game controllers or custom PNG) - Contained -->
    <div 
      v-if="props.navbarBgImage"
      class="absolute inset-0 pointer-events-none select-none"
      :style="{ backgroundImage: `url(${props.navbarBgImage})`, backgroundRepeat: 'no-repeat', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.15 }"
    ></div>
    <div v-else class="absolute inset-0 overflow-hidden pointer-events-none select-none">
      <div class="absolute inset-0 opacity-[0.06] flex flex-wrap gap-8 p-4 rotate-[-1deg] scale-110">
         <div v-for="i in 200" :key="i" class="flex gap-8 items-center">
            <svg class="w-4 h-4 fill-white" viewBox="0 0 24 24"><path d="M21 6H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-10 7H8v3H6v-3H3v-2h3V8h2v3h3v2zm4.5 2c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm4-3c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg>
            <div class="w-2 h-2 rounded-full border-2 border-white/40"></div>
         </div>
      </div>
    </div>

    <div class="w-full flex items-center justify-between relative z-10 px-6 md:px-12 lg:px-20">
      <!-- Logo -->
      <router-link :to="language === 'en' ? { path: '/home', query: { lang: 'en' } } : '/home'" class="flex-shrink-0">
        <img :src="logo" class="h-14 md:h-18 w-auto object-contain transition-transform hover:scale-105" alt="Mito Games" />
      </router-link>

      <!-- Desktop Links -->
      <div class="hidden lg:flex items-center">
        <ul class="flex items-center gap-8 lg:gap-10">
          <li v-for="item in menuItems" :key="item.label" class="flex items-center h-full">
            <button 
              @click="scrollTo(item.id)"
              class="text-[11px] font-black text-white hover:text-white/70 transition-colors uppercase tracking-[0.2em] flex items-center h-10"
            >
              {{ item.label }}
            </button>
          </li>

          <!-- Presskit Dropdown -->
          <li class="relative group flex items-center h-full" @mouseenter="showPresskitDropdown = true" @mouseleave="showPresskitDropdown = false">
            <button 
              class="text-[11px] font-black text-white hover:text-white/70 transition-colors uppercase tracking-[0.2em] flex items-center gap-2 h-10"
            >
              PRESS KIT
              <svg class="w-2.5 h-2.5 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="4" d="M19 9l-7 7-7-7" /></svg>
            </button>
            
            <transition name="dropdown">
              <div v-if="showPresskitDropdown && presskits.length > 0" class="absolute top-full left-0 mt-0 w-64 bg-[#60269E] border border-white/10 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.4)] overflow-hidden backdrop-blur-xl z-[110]">
                <div class="p-2 space-y-1">
                  <button 
                    v-for="pk in presskits" 
                    :key="pk._id"
                    @click="goToPresskit(pk.customUrl)"
                    class="w-full text-left flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-all group/item"
                  >
                    <div class="w-1.5 h-1.5 rounded-full bg-purple-400 group-hover/item:scale-150 transition-transform"></div>
                    <span class="text-[10px] font-black text-white tracking-widest uppercase truncate">{{ pk.title }}</span>
                  </button>
                </div>
              </div>
            </transition>
          </li>
          
          <!-- Language Selector -->
          <li v-if="showLangSelector" class="h-6 w-[1px] bg-white/20 mx-2"></li>
          <li v-if="showLangSelector" class="relative lang-selector-container">
            <button @click.stop="toggleLangDropdown" class="flex items-center gap-3 group">
              <span class="text-[10px] font-black text-white uppercase tracking-widest leading-none">{{ currentLangLabel }}</span>
              <div class="w-8 h-8 rounded-full border-2 border-white/30 overflow-hidden transition-all group-hover:border-white shadow-lg relative">
                <img :src="currentFlag" class="w-full h-full object-cover" :alt="currentLangLabel" />
              </div>
              <svg class="w-3 h-3 text-white/50 transition-transform duration-300" :class="{ 'rotate-180': showLangDropdown }" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M19 9l-7 7-7-7" /></svg>
            </button>

            <!-- Custom Dropdown -->
            <transition name="dropdown">
              <div v-if="showLangDropdown" class="absolute top-full right-0 mt-4 w-48 bg-[#60269E] border border-white/10 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.4)] overflow-hidden backdrop-blur-xl z-[110]">
                <div class="p-2 space-y-1">
                  <button 
                    v-for="lang in languages" 
                    :key="lang.code"
                    @click="changeLang(lang.code)"
                    class="w-full flex items-center gap-3 p-3 rounded-xl transition-all text-left"
                    :class="[props.language === lang.code ? 'bg-white/10' : 'hover:bg-white/5']"
                  >
                    <img :src="lang.flag" class="w-6 h-6 rounded-full border border-white/20 object-cover" />
                    <span class="text-[10px] font-black text-white tracking-widest uppercase">{{ lang.label }}</span>
                    <svg v-if="props.language === lang.code" class="ml-auto w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" /></svg>
                  </button>
                </div>
              </div>
            </transition>
          </li>
        </ul>
      </div>

      <!-- Mobile Toggle -->
      <button @click="showMobileMenu = !showMobileMenu" class="lg:hidden text-white p-2">
        <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>
    </div>

    <!-- Mobile Menu -->
    <div v-if="showMobileMenu" class="lg:hidden fixed inset-0 bg-[#60269E] z-[200] flex flex-col items-center justify-center gap-10 animate-fade-in overflow-hidden">
       <!-- Background Pattern for Mobile -->
       <div class="absolute inset-0 opacity-[0.08] pointer-events-none select-none flex flex-wrap gap-6 p-4 rotate-[-5deg] scale-110">
          <div v-for="i in 100" :key="'mobile-'+i" class="flex gap-6 items-center">
             <svg class="w-4 h-4 fill-white" viewBox="0 0 24 24"><path d="M21 6H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-10 7H8v3H6v-3H3v-2h3V8h2v3h3v2zm4.5 2c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm4-3c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg>
             <div class="w-2 h-2 rounded-full border-2 border-white/40"></div>
          </div>
       </div>

       <button @click="showMobileMenu = false" class="absolute top-6 right-8 text-white p-4 text-4xl relative z-10">&times;</button>
       
       <ul class="text-center space-y-8 relative z-10">
          <li v-for="item in menuItems" :key="item.label">
             <button @click="scrollTo(item.id)" class="text-2xl font-black text-white uppercase tracking-widest">{{ item.label }}</button>
          </li>
          <!-- Mobile Presskits -->
          <li v-if="presskits.length > 0" class="space-y-4">
             <span class="text-[10px] font-black text-white/40 tracking-[0.4em] uppercase">Press Kits</span>
             <div class="flex flex-col gap-4">
                <button 
                  v-for="pk in presskits" 
                  :key="'mob-pk-'+pk._id"
                  @click="goToPresskit(pk.customUrl); showMobileMenu = false"
                  class="text-lg font-black text-white/80 uppercase tracking-widest hover:text-white"
                >
                  {{ pk.title }}
                </button>
             </div>
          </li>
          <li v-if="showLangSelector" class="pt-10 border-t border-white/10 flex flex-col items-center gap-4">
            <div 
               v-for="lang in languages" 
               :key="'mob-'+lang.code"
               @click="showMobileMenu = false"
            >
              <router-link :to="lang.to" class="flex items-center gap-4 py-2" :class="{ 'opacity-40': props.language !== lang.code }">
                <div class="w-10 h-10 rounded-full border-2 border-white/20 overflow-hidden shadow-xl">
                    <img :src="lang.flag" class="w-full h-full object-cover" />
                </div>
                <span class="text-sm font-black text-white tracking-[0.3em] uppercase">{{ lang.label }}</span>
              </router-link>
            </div>
          </li>
       </ul>
    </div>
  </nav>
</template>

<style scoped>
.animate-fade-in {
  animation: fadeIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}
@keyframes fadeIn {
  from { opacity: 0; transform: scale(1.1); }
  to { opacity: 1; transform: scale(1); }
}

/* Dropdown Animation */
.dropdown-enter-active, .dropdown-leave-active {
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
.dropdown-enter-from, .dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(0.95);
}
</style>
